import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Label from "components/Label/Label";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcImage from "shared/NcImage/NcImage";
import {
  selectEditingHolidayPackage,
  setEditingHolidayPackage
} from "application/features/holidayPackage/holidayPackageSlice";
import {
  useUploadHolidayPackageCoverMutation,
  useUploadHolidayPackageGalleryImageMutation,
  useUpdateHolidayPackageMutation
} from "application/features/holidayPackage/holidayPackageApiSlice";

const MediaTab: React.FC<{ setIsDirty: (dirty: boolean) => void }> = ({ setIsDirty }) => {
  const dispatch = useDispatch();
  const editingHolidayPackage = useSelector(selectEditingHolidayPackage);

  const [uploadCover, { isLoading: uploadingCover }] = useUploadHolidayPackageCoverMutation();
  const [uploadGalleryImage, { isLoading: uploadingGallery }] = useUploadHolidayPackageGalleryImageMutation();
  const [updateHolidayPackage] = useUpdateHolidayPackageMutation();

  const [dragActive, setDragActive] = useState(false);
  const galleryInputRef = React.useRef<HTMLInputElement>(null);

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Per favore seleziona un file immagine');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('L\'immagine non può superare 5MB');
      return;
    }

    const packageUuid = editingHolidayPackage?.package_uuid || '';

    const formData = new FormData();
    // Align with the Add flow (PageAddHolidayPackage2) + keep package_uuid for backends that need it
    formData.append('package_uuid', packageUuid);
    formData.append('holiday-package-cover', file);

    try {
      const result = await uploadCover(formData).unwrap();
      const nextCoverUrl = result?.cover_image || result?.result?.s3Location;
      if (!nextCoverUrl) {
        throw new Error('Upload succeeded but no cover URL returned');
      }

      dispatch(setEditingHolidayPackage({
        ...editingHolidayPackage,
        cover_image: nextCoverUrl
      }));
      setIsDirty(true);

      // Persist cover selection so a reload shows the new image even if the upload endpoint
      // only stores the file (and not the package field).
      if (packageUuid) {
        await updateHolidayPackage({ package_uuid: packageUuid, cover_image: nextCoverUrl }).unwrap();
      }

      toast.success('Immagine di copertina aggiornata');
    } catch (error) {
      toast.error('Errore nell\'upload dell\'immagine');
      console.error('Upload error:', error);
    }
  };

  const handleGalleryUpload = async (files: FileList) => {
    const uploadPromises = Array.from(files).map(async (file) => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} non è un file immagine`);
        return null;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} supera 5MB`);
        return null;
      }

      const formData = new FormData();
      formData.append('package_uuid', editingHolidayPackage?.package_uuid || '');
      // Align with the Add flow (PageAddHolidayPackage2)
      formData.append('holiday-package-gallery-image', file);

      try {
        const result = await uploadGalleryImage(formData).unwrap();
        return result?.gallery_image || result?.result?.s3Location;
      } catch (error) {
        toast.error(`Errore nell'upload di ${file.name}`);
        console.error('Upload error:', error);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter(result => result !== null);

    if (successfulUploads.length > 0) {
      const updatedGallery = [
        ...(editingHolidayPackage?.gallery_images || []),
        ...successfulUploads
      ];

      dispatch(setEditingHolidayPackage({
        ...editingHolidayPackage,
        gallery_images: updatedGallery
      }));
      setIsDirty(true);

      toast.success(`${successfulUploads.length} immagini caricate con successo`);
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    const updatedGallery = editingHolidayPackage?.gallery_images?.filter((_, i) => i !== index) || [];
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      gallery_images: updatedGallery
    }));
    setIsDirty(true);
    toast.success('Immagine rimossa dalla galleria');
  };

  const handleSetAsCover = (imageUrl: string) => {
    dispatch(setEditingHolidayPackage({
      ...editingHolidayPackage,
      cover_image: imageUrl
    }));
    setIsDirty(true);
    toast.success('Immagine impostata come copertina');
  };

  const handleClearGallery = () => {
    if (window.confirm('Sei sicuro di voler svuotare tutta la galleria?')) {
      dispatch(setEditingHolidayPackage({
        ...editingHolidayPackage,
        gallery_images: []
      }));
      setIsDirty(true);
      toast.success('Galleria svuotata');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleGalleryUpload(e.dataTransfer.files);
    }
  };

  const handleCoverFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleCoverUpload(e.target.files[0]);
    }
  };

  const handleGalleryFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleGalleryUpload(e.target.files);
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className="text-2xl font-semibold mb-2">Media</h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Gestisci l'immagine di copertina e la galleria fotografica del tuo pacchetto
        </p>
      </div>

      {/* Cover Image */}
      <div>
        <Label>Immagine di copertina *</Label>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 mb-4">
          Questa è l'immagine principale che verrà mostrata nei risultati di ricerca
        </p>

        <div className="bg-white dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl p-8">
          {editingHolidayPackage?.cover_image ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Copertina impostata</span>
                </span>
                <label className="cursor-pointer">
                  <span className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                    Cambia immagine
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverFileSelect}
                    className="hidden"
                    disabled={uploadingCover}
                  />
                </label>
              </div>
              <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-800">
                <NcImage
                  src={editingHolidayPackage.cover_image}
                  alt="Immagine di copertina"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                  COPERTINA
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <label className="cursor-pointer">
                    <div className="bg-white/90 backdrop-blur text-neutral-900 px-6 py-3 rounded-lg font-medium flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Carica nuova copertina</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverFileSelect}
                      className="hidden"
                      disabled={uploadingCover}
                    />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto text-neutral-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <label className="cursor-pointer">
                <ButtonPrimary disabled={uploadingCover} loading={uploadingCover}>
                  {uploadingCover ? 'Caricamento...' : 'Carica immagine di copertina'}
                </ButtonPrimary>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverFileSelect}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-4">
                Formati accettati: JPG, PNG, GIF (Max 5MB)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Gallery Images */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <Label>Galleria fotografica</Label>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Aggiungi altre immagini per mostrare il pacchetto viaggio
            </p>
          </div>
          <ButtonPrimary
            disabled={uploadingGallery}
            loading={uploadingGallery}
            className="!px-6"
            onClick={() => galleryInputRef.current?.click()}
          >
            {uploadingGallery ? 'Caricamento...' : '+ Aggiungi immagini'}
          </ButtonPrimary>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleGalleryFileSelect}
            className="hidden"
          />
        </div>

        {/* Gallery Grid */}
        {editingHolidayPackage?.gallery_images && editingHolidayPackage.gallery_images.length > 0 && (
          <>
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {editingHolidayPackage.gallery_images.length} {editingHolidayPackage.gallery_images.length === 1 ? 'immagine' : 'immagini'} nella galleria
                  </span>
                  <div className="flex items-center space-x-2 text-xs text-neutral-500 dark:text-neutral-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Trascina per riordinare</span>
                  </div>
                </div>
                {editingHolidayPackage.gallery_images.length > 1 && (
                  <button
                    onClick={() => handleClearGallery()}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Svuota galleria
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {editingHolidayPackage.gallery_images.map((image, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-neutral-200 dark:border-neutral-700">
                    <NcImage
                      src={image}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>

                  {/* Image Actions Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button
                        onClick={() => handleRemoveGalleryImage(index)}
                        className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Rimuovi immagine"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Image Index */}
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {index + 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty State */}
        {(!editingHolidayPackage?.gallery_images || editingHolidayPackage.gallery_images.length === 0) && (
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${dragActive
              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
              : "border-neutral-300 dark:border-neutral-600"
              }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <svg className="w-16 h-16 mx-auto text-neutral-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              Nessuna immagine nella galleria
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Trascina le immagini qui o clicca il pulsante per caricarle
            </p>
            <ButtonPrimary
              disabled={uploadingGallery}
              onClick={() => galleryInputRef.current?.click()}
            >
              Scegli immagini
            </ButtonPrimary>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryFileSelect}
              className="hidden"
            />
          </div>
        )}

        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-4">
          {editingHolidayPackage?.gallery_images?.length || 0} immagini nella galleria
        </p>
      </div>

      {/* Guidelines */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
          Linee guida per le immagini
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
          <li>• Usa immagini di alta qualità (minimo 1200x800 pixel)</li>
          <li>• Evita di aggiungere testo o watermark alle immagini</li>
          <li>• Per la copertina, scegli un'immagine che rappresenti al meglio il pacchetto</li>
          <li>• La galleria può contenere fino a 10 immagini</li>
        </ul>
      </div>
    </div>
  );
};

export default MediaTab;